import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import Header from "../components/Header";
import { useAuth } from "../context/AuthContext";
import { criarVaga, analisarVagaTexto, obterEmpresa, skillsSugeridas, AREAS_ATUACAO, SENIORIDADES, CIDADES, BENEFICIOS, caminhoVaga } from "../services/api";

// Formulário de nova vaga REUTILIZÁVEL: usado na página /vagas/nova e no modal "+ Publicar vaga" dentro de /vagas.
// onPublicado(vaga) decide o que fazer ao publicar (página → navega; modal → fecha + recarrega a lista).
export function NovaVagaForm({ onPublicado, compacto = false }) {
  const { user, isEmpresa, isAdmin } = useAuth();
  const podePublicar = isEmpresa || isAdmin; // empresa (e admin operador) publica vagas
  const [f, setF] = useState({ titulo: "", descricao: "", areaAtuacao: "", senioridade: "", cidade: "", modalidade: 0, salarioMin: "", salarioMax: "", tipoContratacao: 0, beneficios: "", skills: "", recrutadorNome: "", recrutadorEmail: "", recrutadorWhatsapp: "", respostaPrazoDias: 15, publicacaoValidadeDias: 30, urlExterna: "" });
  const [erro, setErro] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [verificada, setVerificada] = useState(true); // assume true até carregar (evita flash do aviso); backend é a fonte de verdade
  const [colar, setColar] = useState("");      // texto bruto da vaga (ex.: post do LinkedIn)
  const [analisando, setAnalisando] = useState(false);
  const [aviso, setAviso] = useState(null);
  // Vocabulário dos candidatos: sugerimos o que ELES escreveram (é o que o match compara), sem prender a
  // empresa à lista — se ela precisa de algo que ninguém cadastrou, é só digitar.
  const [sugestoes, setSugestoes] = useState([]);
  useEffect(() => { skillsSugeridas().then(setSugestoes).catch(() => {}); }, []);
  useEffect(() => {
    if (isEmpresa && user?.empresaId) obterEmpresa(user.empresaId).then((e) => setVerificada(e.empresa.verificada)).catch(() => {});
  }, [isEmpresa, user?.empresaId]);

  const set = (c) => (e) => setF({ ...f, [c]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  // Importa o texto colado: a IA segrega nos campos (revisão obrigatória antes de publicar).
  const preencherComIa = async () => {
    setErro(null); setAviso(null); setAnalisando(true);
    try {
      const { sugestoes: s } = await analisarVagaTexto(colar.trim());
      setF((prev) => ({
        ...prev,
        titulo: s.titulo || prev.titulo,
        areaAtuacao: AREAS_ATUACAO.includes(s.areaAtuacao) ? s.areaAtuacao : prev.areaAtuacao,
        senioridade: SENIORIDADES.includes(s.senioridade) ? s.senioridade : prev.senioridade,
        cidade: s.cidade || prev.cidade,
        modalidade: s.modalidade != null ? Number(s.modalidade) : prev.modalidade,
        tipoContratacao: s.tipoContratacao != null ? Number(s.tipoContratacao) : prev.tipoContratacao,
        salarioMin: s.salarioMin != null ? String(s.salarioMin) : prev.salarioMin,
        salarioMax: s.salarioMax != null ? String(s.salarioMax) : prev.salarioMax,
        skills: (s.skills && s.skills.length) ? s.skills.join(", ") : prev.skills,
        beneficios: s.beneficios || prev.beneficios,
        descricao: s.descricao || prev.descricao,
        recrutadorNome: s.recrutadorNome || prev.recrutadorNome,
        recrutadorEmail: s.recrutadorEmail || prev.recrutadorEmail,
        recrutadorWhatsapp: s.recrutadorWhatsapp || prev.recrutadorWhatsapp,
      }));
      setAviso(s.viaIa ? "Preenchi os campos com IA — revise e ajuste antes de publicar. 🙌" : "IA indisponível agora: joguei o texto na descrição; preencha os campos de match (área, senioridade, cidade, skills).");
    } catch (e) { setErro(e.message); } finally { setAnalisando(false); }
  };

  // 💰 SALÁRIO EM REAIS, DO JEITO QUE O BRASILEIRO DIGITA (07/10/2026). O campo era type="number": "4.000"
  // virava 4 (o ponto é decimal para o navegador) e a 1ª vaga da Ápice foi ao ar com "R$ 4 a R$ 5".
  // Agora o ponto é milhar e a vírgula separa centavos ("4.500,00" → 4500).
  const reaisDe = (txt) => {
    let t = String(txt ?? "").replace(/[^\d.,]/g, "");
    if (t.includes(",")) t = t.slice(0, t.lastIndexOf(","));
    t = t.replace(/\./g, "");
    return t === "" ? null : Number(t);
  };

  const publicar = async () => {
    setErro(null);
    const salMin = reaisDe(f.salarioMin), salMax = reaisDe(f.salarioMax);
    const baixo = [salMin, salMax].find((n) => n != null && n > 0 && n < 100);
    if (baixo != null) {
      setErro(`Confira o salário: R$ ${baixo} por mês? Digite o valor cheio, por exemplo 4.000.`);
      return;
    }
    setEnviando(true);
    try {
      const v = await criarVaga({
        titulo: f.titulo.trim(), descricao: f.descricao.trim() || null,
        areaAtuacao: f.areaAtuacao || null, senioridade: f.senioridade || null,
        cidade: f.cidade.trim() || null,
        modalidade: Number(f.modalidade),
        remoto: Number(f.modalidade) === 2,
        salarioMin: salMin,
        salarioMax: salMax,
        tipoContratacao: Number(f.tipoContratacao),
        beneficios: f.beneficios.trim() || null,
        skills: f.skills.split(",").map((s) => s.trim()).filter(Boolean),
        recrutadorNome: f.recrutadorNome.trim() || null,
        recrutadorEmail: f.recrutadorEmail.trim() || null,
        recrutadorWhatsapp: f.recrutadorWhatsapp.trim() || null,
        respostaPrazoDias: Number(f.respostaPrazoDias) || 15,
        publicacaoValidadeDias: Number(f.publicacaoValidadeDias) || 30,
        urlExterna: f.urlExterna.trim() || null,
      });
      onPublicado?.(v);
    } catch (e) { setErro(e.message); } finally { setEnviando(false); }
  };

  const MODALIDADES = ["Presencial", "Híbrido", "Remoto"];
  const TIPOS_CONTRATACAO = [[0, "Não informado"], [1, "CLT"], [2, "PJ"], [3, "Estágio"], [4, "Temporário"], [5, "Trainee"]];

  const inputCls = "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-primary outline-none";
  const labelCls = "block text-sm font-semibold text-gray-700 mb-1";

  if (!podePublicar) return (
    <div className="rounded-2xl border border-gray-200 bg-white p-8 text-center text-gray-600">
      Apenas contas de <strong>empresa</strong> ou <strong>recrutador</strong> publicam vagas. <Link to="/entrar" className="text-primary font-semibold">Entrar</Link>
    </div>
  );

  return (
    <div>
      {!compacto && <h1 className="text-2xl md:text-3xl font-bold text-text">Publicar vaga</h1>}
      <p className="text-gray-600 text-sm mt-1">Preencha área, senioridade, cidade e skills — é o que define o <strong>match</strong> com os candidatos.</p>
      {!verificada && (
        <p className="mt-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 text-sm">
          ⏳ Sua empresa está <strong>em análise</strong> pelo eaiRH. Você poderá <strong>publicar vagas após a aprovação</strong> — pode preparar a vaga abaixo enquanto isso.
        </p>
      )}
      {erro && <p role="alert" className="mt-3 rounded-lg bg-red-50 border border-red-200 text-red-700 px-3 py-2 text-sm">{erro}</p>}
      {aviso && <p className="mt-3 rounded-lg bg-green-50 border border-green-200 text-green-800 px-3 py-2 text-sm">{aviso}</p>}

      {/* Importador: cola o texto da vaga (ex.: post do LinkedIn) → IA preenche os campos */}
      <div className="mt-5 rounded-2xl border border-primary/30 bg-primary/5 p-5 shadow-sm">
        <label className="block text-sm font-semibold text-gray-800 mb-1">✨ Já tem a vaga pronta? Cole o texto que a IA preenche</label>
        <p className="text-xs text-gray-500 mb-2">Cole o anúncio (ex.: do LinkedIn) e clique em preencher — você revisa tudo antes de publicar. O contato do recrutador vai pro campo privado.</p>
        <textarea className={inputCls} rows="4" aria-label="Texto da vaga para importar"
          value={colar} onChange={(e) => setColar(e.target.value)}
          placeholder="Cole aqui o texto completo da vaga..." />
        <button onClick={preencherComIa} disabled={analisando || colar.trim().length < 20}
          className="mt-2 rounded-xl bg-primary hover:opacity-90 disabled:opacity-50 text-white font-semibold px-5 py-2 text-sm">
          {analisando ? "Lendo a vaga..." : "Preencher com IA"}
        </button>
      </div>

      <div className="mt-5 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm space-y-3">
        <div><label className={labelCls}>Título da vaga *</label>
          <input className={inputCls} aria-label="Título da vaga" value={f.titulo} onChange={set("titulo")} placeholder="Ex: Pessoa Desenvolvedora Front-end" /></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div><label className={labelCls}>Área</label>
            <select className={inputCls} aria-label="Área de atuação" value={f.areaAtuacao} onChange={set("areaAtuacao")}>
              <option value="">Selecione</option>{AREAS_ATUACAO.map((a) => <option key={a} value={a}>{a}</option>)}
            </select></div>
          <div><label className={labelCls}>Senioridade</label>
            <select className={inputCls} aria-label="Senioridade" value={f.senioridade} onChange={set("senioridade")}>
              <option value="">Selecione</option>{SENIORIDADES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select></div>
          <div><label className={labelCls}>Cidade</label>
            <input className={inputCls} aria-label="Cidade" list="dl-cidades-vaga" value={f.cidade} onChange={set("cidade")} placeholder="São Paulo" />
            <datalist id="dl-cidades-vaga">{CIDADES.map((c) => <option key={c} value={c} />)}</datalist></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div><label className={labelCls}>Modalidade</label>
            <select className={inputCls} aria-label="Modalidade de trabalho" value={f.modalidade} onChange={set("modalidade")}>
              {MODALIDADES.map((m, i) => <option key={i} value={i}>{m}</option>)}
            </select></div>
          <div><label className={labelCls}>Tipo de contratação</label>
            <select className={inputCls} aria-label="Tipo de contratação" value={f.tipoContratacao} onChange={set("tipoContratacao")}>
              {TIPOS_CONTRATACAO.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
            </select></div>
          <div><label className={labelCls}>Benefícios</label>
            <input className={inputCls} aria-label="Benefícios" list="dl-beneficios" value={f.beneficios} onChange={set("beneficios")} placeholder="VR, VA, plano de saúde..." />
            <datalist id="dl-beneficios">{BENEFICIOS.map((b) => <option key={b} value={b} />)}</datalist></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><label className={labelCls}>Salário mínimo (R$)</label>
            <input type="text" inputMode="decimal" aria-label="Salário mínimo em reais" className={inputCls} value={f.salarioMin} onChange={set("salarioMin")} placeholder="Ex: 4.000 (deixe vazio = a combinar)" /></div>
          <div><label className={labelCls}>Salário máximo (R$)</label>
            <input type="text" inputMode="decimal" aria-label="Salário máximo em reais" className={inputCls} value={f.salarioMax} onChange={set("salarioMax")} placeholder="Ex: 7.000" /></div>
        </div>
        <div><label className={labelCls}>Skills desejadas (separadas por vírgula)</label>
          <input className={inputCls} aria-label="Skills desejadas (separadas por vírgula)" list="skills-candidatos"
                 value={f.skills} onChange={set("skills")} placeholder="React, JavaScript, CSS" />
          <datalist id="skills-candidatos">
            {sugestoes.map((s) => <option key={s.nome} value={s.nome}>{s.candidatos} candidato{s.candidatos > 1 ? "s" : ""}</option>)}
          </datalist>
          {sugestoes.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              <span className="text-[11px] text-gray-500 mr-1">Mais pedidas por quem está cadastrado aqui:</span>
              {sugestoes.slice(0, 12).map((s) => (
                <button key={s.nome} type="button" title={`${s.candidatos} candidato(s) com esta skill`}
                  onClick={() => setF((prev) => {
                    const atuais = prev.skills.split(",").map((x) => x.trim()).filter(Boolean);
                    if (atuais.some((x) => x.toLowerCase() === s.nome.toLowerCase())) return prev;
                    return { ...prev, skills: [...atuais, s.nome].join(", ") };
                  })}
                  className="rounded-full bg-sky-50 text-sky-800 hover:bg-sky-100 px-2.5 py-0.5 text-[12px]">
                  + {s.nome}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><label className={labelCls}>⏱️ Tempo de resposta ao candidato (dias)</label>
            <input type="number" min="1" max="90" step="1" aria-label="Tempo de resposta ao candidato em dias" className={inputCls} value={f.respostaPrazoDias} onChange={set("respostaPrazoDias")} placeholder="15" />
            <p className="text-xs text-gray-500 mt-1">Em quantos dias você se compromete a dar retorno a quem se candidatar. Aparece na vaga (transparência) e <strong>só cobramos você após esse prazo</strong>. Padrão: 15.</p></div>
          <div><label className={labelCls}>📅 Período de publicação (dias)</label>
            <input type="number" min="1" max="180" step="1" aria-label="Período de publicação da vaga em dias" className={inputCls} value={f.publicacaoValidadeDias} onChange={set("publicacaoValidadeDias")} placeholder="30" />
            <p className="text-xs text-gray-500 mt-1">Quanto tempo a vaga fica no ar. Sem atividade após esse prazo, ela <strong>se encerra sozinha</strong> (evita vaga zumbi) — você pode reabri-la quando quiser. Padrão: 30.</p></div>
        </div>
        {/* O campo É a escolha: sem link, a candidatura acontece aqui (e conseguimos avisar cada candidato no
            encerramento). Com link, o botão do eaiRH some e vai tudo pro site da empresa. */}
        <div><label className={labelCls}>🔗 Link para candidatura no site da empresa</label>
          <input className={inputCls} aria-label="Link para candidatura no site da empresa" value={f.urlExterna} onChange={set("urlExterna")} placeholder="https://..." />
          <p className="text-xs text-gray-500 mt-1"><strong>Deixe em branco para receber as candidaturas aqui no eaiRH</strong> — assim conseguimos avisar cada candidato, um a um, quando a vaga for encerrada. Com o link preenchido, a candidatura vai para o seu site e conseguimos avisar só quem demonstrou interesse por aqui.</p></div>
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
          <label className={labelCls}>🔒 Recrutador responsável (privado)</label>
          <p className="text-xs text-gray-500 mb-2">Quem responde pela vaga. <strong>Não aparece para o candidato</strong> — serve para cobrar o retorno de forma nominal (via portal).</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <input className={inputCls} aria-label="Nome do recrutador (privado)" placeholder="Nome" value={f.recrutadorNome} onChange={set("recrutadorNome")} />
            <input className={inputCls} aria-label="E-mail do recrutador (privado)" placeholder="E-mail" value={f.recrutadorEmail} onChange={set("recrutadorEmail")} />
            <input className={inputCls} aria-label="WhatsApp do recrutador (privado)" placeholder="WhatsApp" value={f.recrutadorWhatsapp} onChange={set("recrutadorWhatsapp")} />
          </div>
        </div>
        <div><label className={labelCls}>Descrição</label>
          <textarea className={inputCls} aria-label="Descrição da vaga" rows="4" value={f.descricao} onChange={set("descricao")} placeholder="Responsabilidades, requisitos..." /></div>
        <button onClick={publicar} disabled={enviando || !f.titulo.trim()}
          className="rounded-xl bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white font-semibold px-6 py-2.5">
          {enviando ? "Publicando..." : "Publicar vaga"}
        </button>
      </div>
    </div>
  );
}

export default function NovaVagaPage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-3xl mx-auto px-4 md:px-6 py-8 pb-28 md:pb-8">
        <NovaVagaForm onPublicado={(v) => navigate(caminhoVaga(v))} />
      </main>
    </div>
  );
}
